import {Box, Typography, Button} from '@mui/material';
import {ReactNode} from 'react';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import {neutral} from 'src/theme';

interface EmptyStateProps {
  /** Custom icon — defaults to a generic inbox icon */
  icon?: ReactNode;
  title: string;
  description?: string;
  /** Primary action button */
  action?: {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
  };
  /** Compact mode for use inside DataGrid overlays */
  compact?: boolean;
}

/**
 * Unified empty state for all list pages, tables, and panels.
 *
 * Replaces: all ad-hoc "No Events", "No users", "No directories yet" patterns.
 *
 * Usage:
 *   <EmptyState
 *     title="No devices yet"
 *     description="Connect an intercom device to start managing access."
 *     action={{ label: "Add Device", onClick: () => setOpen(true), icon: <AddIcon /> }}
 *   />
 *
 * For DataGrid:
 *   slots={{ noRowsOverlay: () => <EmptyState compact title="No events" /> }}
 */
export default function EmptyState({icon, title, description, action, compact}: EmptyStateProps) {
  if (compact) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          pt: 8,
          pb: 4,
          gap: 1,
        }}
      >
        <Box sx={{color: neutral[300], mb: 0.5}}>
          {icon || <InboxOutlinedIcon sx={{fontSize: 40}} />}
        </Box>
        <Typography variant="subtitle1" color="text.secondary">
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{maxWidth: 320, textAlign: 'center'}}>
            {description}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 10,
        gap: 1.5,
      }}
    >
      <Box sx={{color: neutral[300], mb: 1}}>
        {icon || <InboxOutlinedIcon sx={{fontSize: 56}} />}
      </Box>
      <Typography variant="h5" color="text.primary">
        {title}
      </Typography>
      {description && (
        <Typography variant="body1" color="text.secondary" sx={{maxWidth: 400, textAlign: 'center'}}>
          {description}
        </Typography>
      )}
      {action && (
        <Button
          variant="contained"
          startIcon={action.icon}
          onClick={action.onClick}
          sx={{mt: 2}}
        >
          {action.label}
        </Button>
      )}
    </Box>
  );
}
